'use client';

import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './AlertDialog/AlertDialog';
import { buttonVariants } from './Button';
import { useTranslation } from '../lib/i18n/TranslationContext';

type NavigateEventLike = Event & {
	navigationType: 'reload' | 'push' | 'replace' | 'traverse';
	destination: { key: string; sameDocument: boolean };
};

type NavigationLike = {
	addEventListener: (type: 'navigate', cb: (e: NavigateEventLike) => void) => void;
	removeEventListener: (type: 'navigate', cb: (e: NavigateEventLike) => void) => void;
	traverseTo: (key: string) => { committed: Promise<unknown>; finished: Promise<unknown> };
};

const getNavigation = () => (window as unknown as { navigation?: NavigationLike }).navigation;

type Pending = { run: () => void; cancel?: () => void };

const tokens = new Set<symbol>();
const listeners = new Set<() => void>();
let pending: Pending | null = null;
let navigateFn: ((href: string) => void) | null = null;
let attempts = 0;
const getAttempts = () => attempts;
const getServerAttempts = () => 0;
let bypassUntil = 0;

const subscribe = (l: () => void) => {
	listeners.add(l);
	return () => {
		listeners.delete(l);
	};
};
const notify = () => listeners.forEach((l) => l());
const getSnapshot = () => pending;
const getServerSnapshot = (): Pending | null => null; 

export function isNavigationBlocked() {
	return tokens.size > 0;
}

let prontas = 0;
let explicitas = 0;

const incProntas = (n: number) => {
	prontas += n;
};
const incExplicitas = (n: number) => {
	explicitas += n;
};

function canPrompt() {
	if (tokens.size === 0) return false;
	if (prontas === 0) {
		console.warn('[empreender-ui] nenhum aviso de navegação montado (<NavigationBlocker /> ou <ActionBar open />) — a navegação seguiu sem avisar.');
		return false;
	}
	return true;
}

function onClickCapture(e: MouseEvent) {
	if (Date.now() < bypassUntil) return;
	if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

	const el = e.target instanceof Element ? e.target.closest('a[href]') : null;
	const raw = el?.getAttribute('href');
	if (!el || !raw) return;

	const alvo = el.getAttribute('target');
	if (el.hasAttribute('download') || (alvo && alvo !== '_self')) return;

	let url: URL;
	try {
		url = new URL(raw, window.location.href);
	} catch {
		return;
	}
	if (url.protocol !== 'http:' && url.protocol !== 'https:') return; 
	if (url.href === window.location.href) return;

	const mesmaPagina = url.origin === window.location.origin && url.pathname === window.location.pathname && url.search === window.location.search;
	if (mesmaPagina && !url.hash.startsWith('#/')) return; // #secao é âncora; #/rota é hash router

	if (!canPrompt()) return;

	e.preventDefault();

	confirmNavigation(() => {
		if (url.origin === window.location.origin && !mesmaPagina && navigateFn) navigateFn(url.pathname + url.search + url.hash);
		else if (el instanceof HTMLElement && el.isConnected) el.click();
		else window.location.assign(url.href);
	});
}

function onNavigate(e: NavigateEventLike) {
	if (Date.now() < bypassUntil) return;
	if (e.navigationType !== 'traverse' || !e.destination.sameDocument || !e.cancelable) return;
	if (!canPrompt()) return;

	const key = e.destination.key;
	e.preventDefault();

	confirmNavigation(() => {
		const r = getNavigation()?.traverseTo(key);
		r?.committed.catch(() => {});
		r?.finished.catch(() => {});
	});
}


function onBeforeUnload(e: BeforeUnloadEvent) {
	if (Date.now() < bypassUntil) return;
	e.preventDefault();
	e.returnValue = true; // legado Chrome/Edge < 119
}

export function blockNavigation(): () => void {
	if (typeof document === 'undefined') return () => {};

	const token = Symbol('empreender-nav-block');
	tokens.add(token);
	document.addEventListener('click', onClickCapture, true);
	window.addEventListener('beforeunload', onBeforeUnload);
	getNavigation()?.addEventListener('navigate', onNavigate);

	return () => {
		if (!tokens.delete(token) || tokens.size > 0) return;
		document.removeEventListener('click', onClickCapture, true);
		window.removeEventListener('beforeunload', onBeforeUnload);
		getNavigation()?.removeEventListener('navigate', onNavigate);
		cancelNavigation();
	};
}

export function useBlockNavigation(when: boolean) {
	useEffect(() => (when ? blockNavigation() : undefined), [when]);
}

export function confirmNavigation(run: () => void, cancel?: () => void) {
	if (!canPrompt()) return run();

	attempts += 1;

	pending = { run, cancel };
	notify();
}

function cancelNavigation() {
	const p = pending;
	if (!p) return;
	pending = null;
	notify();
	p.cancel?.();
}

function proceedNavigation() {
	takePendingNavigation()();
}

function takePendingNavigation(): () => void {
	const p = pending;
	if (!p) return () => {};
	pending = null;
	notify();
	return () => {
		bypassUntil = Date.now() + 1000;
		window.setTimeout(p.run, 0);
	};
}


export type NavigationBlockerProps = {
	title?: string;
	description?: string;
	navigate?: (href: string) => void;
	children?: (p: { open: boolean; cancelar: () => void; prosseguir: () => void }) => ReactNode;
};

const getExplicitas = () => explicitas;
const getServerExplicitas = () => 0;

function useContador(inc: (n: number) => void, ativo: boolean) {
	useEffect(() => {
		if (!ativo) return;
		inc(1);
		notify();
		return () => {
			inc(-1);
			notify();
		};
	}, [inc, ativo]);
}

export function useNavigationPrompt(ativo = true) {
	const atual = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
	const tentativas = useSyncExternalStore(subscribe, getAttempts, getServerAttempts);
	useContador(incExplicitas, ativo);
	useContador(incProntas, ativo);
	return { pending: atual !== null, attempts: tentativas, takeNavigation: takePendingNavigation };
}

function BlockerUI({ title, description, navigate, children }: NavigationBlockerProps) {
	const { t } = useTranslation();
	const atual = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
	const aberto = atual !== null;

	useContador(incProntas, true);

	useEffect(() => {
		const fn = navigate ?? null;
		navigateFn = fn;
		return () => {
			if (navigateFn === fn) navigateFn = null;
		};
	}, [navigate]);

	if (children) return <>{children({ open: aberto, cancelar: cancelNavigation, prosseguir: proceedNavigation })}</>;

	return (
		<AlertDialog
			open={aberto}
			onOpenChange={(open) => {
				if (!open) cancelNavigation();
			}}>
			<AlertDialogContent className='w-full sm:max-w-md'>
				<AlertDialogHeader>
					<AlertDialogTitle>{title ?? t('Deseja sair desta página?')}</AlertDialogTitle>
					<AlertDialogDescription>{description ?? t('As alterações não salvas serão perdidas.')}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel className={buttonVariants({ variant: 'outline' })}>{t('Cancelar')}</AlertDialogCancel>
					<AlertDialogAction className={buttonVariants()} onClick={proceedNavigation}>
						{t('Prosseguir')}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

export function NavigationBlocker(props: NavigationBlockerProps) {
	useContador(incExplicitas, true);
	return <BlockerUI {...props} />;
}

export function NavigationBlockerAuto(props: NavigationBlockerProps) {
	const explicito = useSyncExternalStore(subscribe, getExplicitas, getServerExplicitas);
	if (explicito > 0) return null;
	return <BlockerUI {...props} />;
}
